import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import axios from "axios";
import { API } from "@/App";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const NWI_TILE_URL =
  "https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0";

export function calcAcres(polygon) {
  if (!polygon?.coordinates?.[0]) return 0;
  const coords = polygon.coordinates[0];
  if (coords.length < 3) return 0;
  let total = 0;
  const n = coords.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = coords[i][0] * Math.cos((coords[i][1] * Math.PI) / 180) * 111320;
    const yi = coords[i][1] * 110540;
    const xj = coords[j][0] * Math.cos((coords[j][1] * Math.PI) / 180) * 111320;
    const yj = coords[j][1] * 110540;
    total += xi * yj - xj * yi;
  }
  return (Math.abs(total) / 2) * 0.000247105;
}

function calcCenter(polygons) {
  let sumLon = 0, sumLat = 0, count = 0;
  polygons.forEach((p) => {
    if (p?.coordinates?.[0]) {
      p.coordinates[0].forEach(([lon, lat]) => { sumLon += lon; sumLat += lat; count++; });
    }
  });
  return count > 0 ? [sumLon / count, sumLat / count] : [-78.6382, 35.7796];
}

/**
 * Custom hook for managing the Mapbox drawing interface in the Change Order Builder.
 * Uses refs for polygons/showWetlands in stable callbacks to prevent stale closures.
 * @param {object|null} quote - The parent quote (used to center map & show reference polygons)
 */
export function useChangeOrderMap(quote) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);

  // Refs for stable callbacks (avoids stale closures in reloadCustomLayers)
  const polygonsRef = useRef([]);
  const showWetlandsRef = useRef(true);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [showWetlands, setShowWetlands] = useState(true);

  // Keep refs in sync with state
  useEffect(() => { polygonsRef.current = polygons; }, [polygons]);
  useEffect(() => { showWetlandsRef.current = showWetlands; }, [showWetlands]);

  const totalAcres = polygons.reduce((sum, p) => sum + calcAcres(p), 0);

  // Stable callback — reads from refs, not state — safe to pass to MapStyleSwitcher
  const reloadCustomLayers = useCallback(() => {
    if (!mapRef.current) return;
    const m = mapRef.current;
    const currentPolygons = polygonsRef.current;
    const currentShowWetlands = showWetlandsRef.current;

    if (!m.getSource("nwi-wetlands")) {
      m.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
      m.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      if (!currentShowWetlands) m.setLayoutProperty("nwi-wetlands-layer", "visibility", "none");
    }

    const quotePolygons = quote?.polygons || [];
    if (quotePolygons.length > 0 && !m.getSource("quote-areas")) {
      const features = quotePolygons.map((poly, i) => ({ type: "Feature", geometry: poly, properties: { label: `Original Area ${i + 1}` } }));
      m.addSource("quote-areas", { type: "geojson", data: { type: "FeatureCollection", features } });
      m.addLayer({ id: "quote-areas-fill", type: "fill", source: "quote-areas", paint: { "fill-color": "#3b82f6", "fill-opacity": 0.15 } });
      m.addLayer({ id: "quote-areas-line", type: "line", source: "quote-areas", paint: { "line-color": "#3b82f6", "line-width": 2, "line-dasharray": [3, 2] } });
    }

    if (drawRef.current && currentPolygons.length > 0) {
      currentPolygons.forEach((poly) => {
        if (poly?.coordinates) drawRef.current.add({ type: "Feature", geometry: poly, properties: {} });
      });
    }

    if (quote?.property_address && !m.getSource("parcels")) {
      axios.post(`${API}/parcels/by-address`, { address: quote.property_address })
        .then((resp) => {
          if (resp.data.features?.length > 0 && mapRef.current && !mapRef.current.getSource("parcels")) {
            mapRef.current.addSource("parcels", { type: "geojson", data: { type: "FeatureCollection", features: resp.data.features } });
            mapRef.current.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 } });
            mapRef.current.addLayer({ id: "parcels-line", type: "line", source: "parcels", paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 } });
          }
        })
        .catch(() => {});
    }
  }, [quote]);

  const initMap = useCallback(async () => {
    if (mapRef.current || !mapContainerRef.current || !MAPBOX_TOKEN || !quote) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    let center = [-78.6382, 35.7796];
    let zoom = 14;
    const quotePolygons = quote.polygons || [];

    if (quotePolygons.length > 0) {
      center = calcCenter(quotePolygons);
      zoom = 16;
    } else if (quote.property_address) {
      try {
        const resp = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(quote.property_address)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
        );
        const geoData = await resp.json();
        if (geoData.features?.[0]) center = geoData.features[0].center;
      } catch (err) { console.error("Geocoding failed:", err?.message); }
    }

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center,
      zoom,
    });

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: "simple_select",
      styles: [
        { id: "gl-draw-polygon-fill", type: "fill", filter: ["all", ["==", "$type", "Polygon"]], paint: { "fill-color": "#f97316", "fill-opacity": 0.3 } },
        { id: "gl-draw-polygon-stroke", type: "line", filter: ["all", ["==", "$type", "Polygon"]], paint: { "line-color": "#f97316", "line-width": 3 } },
        { id: "gl-draw-polygon-and-line-vertex-active", type: "circle", filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]], paint: { "circle-radius": 6, "circle-color": "#fbbf24" } },
      ],
    });

    mapRef.current.addControl(drawRef.current, "top-right");
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    mapRef.current.on("load", () => {
      setMapLoaded(true);

      if (!mapRef.current.getSource("nwi-wetlands")) {
        mapRef.current.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
        mapRef.current.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      }

      if (quotePolygons.length > 0 && !mapRef.current.getSource("quote-areas")) {
        const features = quotePolygons.map((poly, i) => ({ type: "Feature", geometry: poly, properties: { label: `Original Area ${i + 1}` } }));
        mapRef.current.addSource("quote-areas", { type: "geojson", data: { type: "FeatureCollection", features } });
        mapRef.current.addLayer({ id: "quote-areas-fill", type: "fill", source: "quote-areas", paint: { "fill-color": "#3b82f6", "fill-opacity": 0.15 } });
        mapRef.current.addLayer({ id: "quote-areas-line", type: "line", source: "quote-areas", paint: { "line-color": "#3b82f6", "line-width": 2, "line-dasharray": [3, 2] } });
      }

      if (quote.property_address && !mapRef.current.getSource("parcels")) {
        axios.post(`${API}/parcels/by-address`, { address: quote.property_address })
          .then((resp) => {
            const data = resp.data;
            if (data.features?.length > 0 && mapRef.current && !mapRef.current.getSource("parcels")) {
              mapRef.current.addSource("parcels", { type: "geojson", data: { type: "FeatureCollection", features: data.features } });
              mapRef.current.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 } });
              mapRef.current.addLayer({ id: "parcels-line", type: "line", source: "parcels", paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 } });
            }
          })
          .catch(() => {});
      }
    });

    const syncFromDraw = () => {
      if (!drawRef.current) return;
      const all = drawRef.current.getAll();
      const polys = all.features.filter((f) => f.geometry.type === "Polygon").map((f) => f.geometry);
      setPolygons(polys);
    };

    mapRef.current.on("draw.create", () => { syncFromDraw(); setIsDrawing(false); });
    mapRef.current.on("draw.update", syncFromDraw);
    mapRef.current.on("draw.delete", syncFromDraw);
  }, [quote]);

  useEffect(() => {
    if (quote) initMap();
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [quote, initMap]);

  const startDrawing = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.changeMode("draw_polygon");
      setIsDrawing(true);
      toast.info("Click on the map to draw area boundaries. Double-click to finish.");
    }
  }, []);

  const cancelDrawing = useCallback(() => {
    if (drawRef.current) { drawRef.current.changeMode("simple_select"); setIsDrawing(false); }
  }, []);

  const removePolygon = useCallback((index) => {
    if (drawRef.current) {
      const all = drawRef.current.getAll();
      const polyFeatures = all.features.filter((f) => f.geometry.type === "Polygon");
      if (polyFeatures[index]) drawRef.current.delete(polyFeatures[index].id);
    }
    setPolygons((prev) => prev.filter((_, i) => i !== index));
    toast.info("Area removed");
  }, []);

  const toggleWetlands = useCallback(() => {
    if (!mapRef.current?.getLayer("nwi-wetlands-layer")) return;
    const next = !showWetlandsRef.current;
    mapRef.current.setLayoutProperty("nwi-wetlands-layer", "visibility", next ? "visible" : "none");
    showWetlandsRef.current = next;
    setShowWetlands(next);
  }, []);

  return {
    mapContainerRef,
    mapRef,
    mapLoaded,
    isDrawing,
    polygons,
    showWetlands,
    totalAcres,
    startDrawing,
    cancelDrawing,
    removePolygon,
    toggleWetlands,
    reloadCustomLayers,
  };
}
